import React, { useState } from 'react';
import { AuthenticatedTemplate } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { ExpandLess, ExpandMore, Code as CodeIcon, MedicalServices as MedicalServicesIcon, Description as DescriptionIcon, Policy as PolicyIcon, PendingActions as PendingIcon, Cancel as DeniedIcon, CheckCircle as ApprovedIcon } from '@mui/icons-material';
import { Collapse, List, ListItemButton, ListItemIcon, ListItemText, Box } from '@mui/material';
import { useAppSelector } from '../../../../../app/hooks';
import { useGetMyProfileQuery } from '../../../../../services/ProfileService';
import { hasCaseManagementAccess, hasClinicalPolicyAccess } from '../../../../profile/utils/profileUtils';

// Types
interface MenuItemConfig {
    id: string;
    title: string;
    icon: React.ComponentType<{ sx?: any }>;
    type?: string;
    route: string;
    children?: MenuItemConfig[];
    isNested?: boolean;
}

// Constants
const MENU_STYLES = {
    navList: { 
        border: 'black solid 1px' 
    },
    listItem: { 
        marginTop: '0px' 
    },
    nestedListItem: { 
        marginTop: '0px',
        paddingLeft: '48px' // Indent nested items
    },
    icon: { 
        color: 'white' 
    },
    countContainer: { 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        width: '100%'
    }
} as const;

const MENU_ITEMS: MenuItemConfig[] = [
    {
        id: 'policies',
        title: 'Policies',
        icon: PolicyIcon,
        route: '/case-management/policies'
    },
    {
        id: 'coding-review',
        title: 'Coding Review',
        icon: CodeIcon,
        type: 'Coding Review',
        route: '/case-management/worklist'
    },
    {
        id: 'clinical-review',
        title: 'Clinical Review',
        icon: MedicalServicesIcon,
        type: 'Clinical Review',
        route: '/case-management/worklist'
    },
    {
        id: 'docs-required',
        title: 'Docs Required',
        icon: DescriptionIcon,
        type: 'Documents Needed',
        route: '/case-management/worklist'
    },
    {
        id: 'pending',
        title: 'Pending',
        icon: PendingIcon,
        route: '', // Parent item doesn't navigate
        children: [
            {
                id: 'clinical-pending',
                title: 'Clinical Pending',
                icon: MedicalServicesIcon,
                type: 'Clinical Pending',
                route: '/case-management/worklist',
                isNested: true
            },
            {
                id: 'coding-pending',
                title: 'Coding Pending',
                icon: CodeIcon,
                type: 'Coding Pending',
                route: '/case-management/worklist',
                isNested: true
            }
        ]
    },
    {
        id: 'denied',
        title: 'Denied',
        icon: DeniedIcon,
        route: '', // Parent item doesn't navigate
        children: [
            {
                id: 'clinical-denied',
                title: 'Clinical Denied',
                icon: MedicalServicesIcon,
                type: 'Clinical Denied',
                route: '/case-management/worklist',
                isNested: true
            },
            {
                id: 'coding-denied',
                title: 'Coding Denied',
                icon: CodeIcon,
                type: 'Coding Denied',
                route: '/case-management/worklist',
                isNested: true
            }
        ]
    },
    {
        id: 'approved',
        title: 'Approved',
        icon: ApprovedIcon,
        route: '', // Parent item doesn't navigate
        children: [
            {
                id: 'clinical-approved',
                title: 'Clinical Approved',
                icon: MedicalServicesIcon,
                type: 'Clinical Approved',
                route: '/case-management/worklist',
                isNested: true
            },
            {
                id: 'coding-approved',
                title: 'Coding Approved',
                icon: CodeIcon,
                type: 'Coding Approved',
                route: '/case-management/worklist',
                isNested: true
            }
        ]
    }
];

// Utility functions
const buildNavigationUrl = (route: string, params: Record<string, string>): string => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value) searchParams.append(key, value);
    });
    return `${route}?${searchParams.toString()}`;
};

// Sub-components
interface MenuItemProps {
    item: MenuItemConfig;
    onClick: () => void;
}

interface NestedMenuItemProps {
    item: MenuItemConfig;
}

const NestedMenuItem: React.FC<NestedMenuItemProps> = ({ item }) => {
    const navigate = useNavigate();
    const selectedCenter = useAppSelector(state => state.center.selectedCenter);
    const { data: profile } = useGetMyProfileQuery();
    const company = profile?.organization || '';

    const handleClick = () => {
        if (!item.route) return;
        
        const baseParams = { company };
        const params = item.type 
            ? { ...baseParams, center: selectedCenter || '', type: item.type }
            : baseParams;
        
        const url = buildNavigationUrl(item.route, params);
        navigate(url);
    };

    const { title, icon: Icon } = item;

    return (
        <ListItemButton sx={MENU_STYLES.nestedListItem} onClick={handleClick}>
            <ListItemIcon>
                <Icon sx={MENU_STYLES.icon} />
            </ListItemIcon>
            <ListItemText primary={title} />
        </ListItemButton>
    );
};

const MenuItem: React.FC<MenuItemProps> = ({ item, onClick }) => {
    const { title, icon: Icon, children } = item;
    const [nestedOpen, setNestedOpen] = useState(false);

    const hasChildren = children && children.length > 0;

    const handleClick = () => {
        if (hasChildren) {
            setNestedOpen(!nestedOpen);
        } else {
            onClick();
        }
    };

    return (
        <>
            <ListItemButton 
                sx={item.isNested ? MENU_STYLES.nestedListItem : MENU_STYLES.listItem} 
                onClick={handleClick}
            >
                <ListItemIcon>
                    <Icon sx={MENU_STYLES.icon} />
                </ListItemIcon>
                <Box sx={MENU_STYLES.countContainer}>
                    <ListItemText primary={title} />
                    {hasChildren && (nestedOpen ? <ExpandLess /> : <ExpandMore />)}
                </Box>
            </ListItemButton>

            {hasChildren && (
                <Collapse in={nestedOpen} timeout="auto" unmountOnExit>
                    {children.map((childItem) => (
                        <NestedMenuItem
                            key={childItem.id}
                            item={childItem}
                        />
                    ))}
                </Collapse>
            )}
        </>
    );
};

// Main component
export const CaseManagementMenu: React.FC = () => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    
    const selectedCenter = useAppSelector(state => state.center.selectedCenter);
    const { data: profile } = useGetMyProfileQuery();
    const company = profile?.organization || '';

    const handleItemClick = (item: MenuItemConfig): void => {
        // Don't navigate if item has children or no route
        if (item.children?.length || !item.route) return;
        
        const baseParams = { company };
        const params = item.type 
            ? { ...baseParams, center: selectedCenter || '', type: item.type }
            : baseParams;
        
        const url = buildNavigationUrl(item.route, params);
        navigate(url);
    };

    const toggleMenu = (): void => setOpen(!open);

    // Only show if user has CaseManagement claim access
    if (!hasCaseManagementAccess(profile)) {
        return null;
    }

    // Filter menu items based on user permissions
    const filteredMenuItems = MENU_ITEMS.filter(item => {
        // Filter out Policies if user doesn't have Clinical Policy claim
        if (item.id === 'policies') {
            return hasClinicalPolicyAccess(profile);
        }
        return true;
    });

    return (
        <AuthenticatedTemplate>
            <List component="nav" sx={MENU_STYLES.navList}>
                <ListItemButton onClick={toggleMenu}>
                    <ListItemText primary="Case Management" />
                    {open ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={open} timeout="auto" unmountOnExit>
                    {filteredMenuItems.map((item) => (
                        <MenuItem
                            key={item.id}
                            item={item}
                            onClick={() => handleItemClick(item)}
                        />
                    ))}
                </Collapse>
            </List>
        </AuthenticatedTemplate>
    );
};
